import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  SelectChangeEvent,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  EventAvailable,
  Description,
  CheckCircle,
  CalendarMonth,
  AccessTime,
} from '@mui/icons-material';

// Web3Forms configuration
const WEB3FORMS_ACCESS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY || '93bbd2ae-0145-4e58-b5ea-79d15a8faccc';
const WEB3FORMS_API_URL = 'https://api.web3forms.com/submit';

interface ConsultationFormData {
  name: string;
  email: string;
  phone: string;
  visaType: string;
  preferredDate: string;
  preferredTime: string;
  message: string;
}

const visaTypes = [
  'Student Visa',
  'Work Visa',
  'Family Visa',
  'Visitor Visa',
  'Business Visa',
  'Settlement Visa',
  'Other',
];

const timeSlots = [
  '09:00 AM',
  '10:00 AM',
  '11:00 AM',
  '12:00 PM',
  '02:00 PM',
  '03:00 PM',
  '04:00 PM',
  '05:00 PM',
];

const benefits = [
  'Free 30-minute consultation with our immigration experts',
  'Comprehensive assessment of your immigration options',
  'Detailed explanation of the visa application process',
  'Personalized advice tailored to your situation',
  'Clear guidance on documentation requirements',
  'Transparent discussion about costs and timelines',
];

const Consultation = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [formData, setFormData] = useState<ConsultationFormData>({
    name: '',
    email: '',
    phone: '',
    visaType: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
  });

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      visaType: '',
      preferredDate: '',
      preferredTime: '',
      message: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Create FormData for Web3Forms (requires FormData, not JSON)
      const formDataToSend = new FormData(e.currentTarget);
      
      // Add Web3Forms required fields - let Web3Forms use its default formatting
      formDataToSend.append('access_key', WEB3FORMS_ACCESS_KEY);
      formDataToSend.append('subject', `Consultation Request: ${formData.visaType || 'General Inquiry'}`);
      formDataToSend.append('from_name', 'Chishty UK Immigration Law Website');
      
      // Add honeypot field for spam protection (hidden field)
      formDataToSend.append('botcheck', '');
      
      // Send to Web3Forms API
      const response = await fetch(WEB3FORMS_API_URL, {
        method: 'POST',
        body: formDataToSend,
      });
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Consultation form submitted successfully:', data);
        setSnackbar({
          open: true,
          message: 'Your consultation request has been submitted successfully! We will contact you soon to confirm your appointment.',
          severity: 'success'
        });
        resetForm();
      } else {
        throw new Error(data.message || 'Failed to submit consultation request');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit your consultation request. Please try again later.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ py: { xs: 4, sm: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6, md: 8 } }}>
          <Typography 
            variant={isMobile ? "h3" : "h1"} 
            gutterBottom
            sx={{ fontWeight: 600 }}
          >
            Book a Free Consultation
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h5"} 
            color="textSecondary" 
            sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}
          >
            Get expert advice on your UK immigration journey
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} direction={isTablet ? "column-reverse" : "row"}>
          {/* Benefits Section */}
          <Grid item xs={12} md={5}>
            <Paper 
              sx={{ 
                p: { xs: 3, sm: 4 }, 
                height: '100%',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            >
              <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
                What You'll Get
              </Typography>
              <Stack spacing={2} sx={{ mt: 3 }}>
                {benefits.map((benefit, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: { xs: 1, sm: 2 }
                    }}
                  >
                    <CheckCircle 
                      color="primary" 
                      sx={{ 
                        mt: 0.2,
                        fontSize: { xs: '1.2rem', sm: '1.5rem' }
                      }} 
                    />
                    <Typography 
                      sx={{ 
                        fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}
                    >
                      {benefit}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>

          {/* Consultation Form */}
          <Grid item xs={12} md={7}>
            <Paper 
              sx={{ 
                p: { xs: 3, sm: 4 },
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
            >
              <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
                Schedule Your Consultation
              </Typography>
              <form onSubmit={handleSubmit}>
                {/* Honeypot field for spam protection (hidden) */}
                <input type="checkbox" name="botcheck" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name}
                      onChange={handleTextChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleTextChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleTextChange}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required size={isMobile ? "small" : "medium"}>
                      <InputLabel>Visa Type</InputLabel>
                      <Select
                        name="visaType"
                        value={formData.visaType}
                        label="Visa Type"
                        onChange={handleSelectChange}
                      >
                        {visaTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Preferred Date"
                      name="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={handleTextChange}
                      required
                      InputLabelProps={{ shrink: true }}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: (
                          <CalendarMonth sx={{ mr: 1, color: 'text.secondary', fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required size={isMobile ? "small" : "medium"}>
                      <InputLabel>Preferred Time</InputLabel>
                      <Select
                        name="preferredTime"
                        value={formData.preferredTime}
                        label="Preferred Time"
                        onChange={handleSelectChange}
                        startAdornment={<AccessTime sx={{ mr: 1, color: 'text.secondary', fontSize: isMobile ? '1.2rem' : '1.5rem' }} />}
                      >
                        {timeSlots.map((time) => (
                          <MenuItem key={time} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Additional Information"
                      name="message"
                      multiline
                      rows={isMobile ? 3 : 4}
                      value={formData.message}
                      onChange={handleTextChange}
                      helperText="Please provide any specific questions or concerns you'd like to discuss"
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={loading}
                      startIcon={<EventAvailable />}
                      sx={{
                        py: { xs: 1.2, sm: 1.5 },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
                        }
                      }}
                    >
                      {loading ? 'Submitting...' : 'Book Consultation'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Confirmation Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Consultation; 